import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Menu, LogOut, KeyRound, Wifi } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { TENANT_NAV, ADMIN_NAV, homePath } from "@/components/nav";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { NativeSelect } from "@/components/common";
import { api, errMsg } from "@/lib/api";

function TenantSwitcher() {
  const { can, tenant, switchTenant } = useAuth();
  const nav = useNavigate();
  const [list, setList] = useState([]);
  useEffect(() => {
    api.get("/auth/tenants").then((r) => setList(r.data)).catch(() => {});
  }, []);
  const go = async (id) => {
    try {
      const s = await switchTenant(id);
      nav(homePath(s));
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <NativeSelect value={tenant?.id || ""} onChange={(e) => go(e.target.value)} className="max-w-[110px] sm:max-w-[190px] h-9 text-xs font-semibold border-emerald-300 bg-emerald-50" data-testid="tenant-switcher">
      <option value="">{can("platform:view") ? "Panel Admin" : "— Pilih perusahaan —"}</option>
      {list.map((t) => <option key={t.id} value={t.id}>{t.name}{t.active ? "" : " (nonaktif)"}</option>)}
    </NativeSelect>
  );
}

function NavCount({ url, tid }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let live = true;
    const get = () => api.get(url).then((r) => live && setN(r.data.count)).catch(() => {});
    get();
    const t = setInterval(get, 60000);
    window.addEventListener("revision-queue-changed", get);
    return () => { live = false; clearInterval(t); window.removeEventListener("revision-queue-changed", get); };
  }, [url]);
  return n > 0 ? <span className="ml-auto text-[10px] font-bold bg-amber-400 text-slate-900 rounded-full px-1.5" data-testid={tid}>{n}</span> : null;
}

function NavList({ items, onNavigate }) {
  const { can } = useAuth();
  return (
    <nav className="flex flex-col gap-0.5 px-2" data-testid="app-sidebar-nav">
      {items.filter((i) => can(i.perm)).map((i, idx) => (
        <NavLink
          key={i.to}
          to={i.to}
          end={i.to === "/" || i.to === "/admin"}
          onClick={onNavigate}
          data-testid={i.tid}
          className={({ isActive }) =>
            `group flex items-center gap-3 px-3 py-2 text-[13px] rounded-sm transition-colors ${
              isActive ? "bg-emerald-600 text-white" : "text-emerald-50/85 hover:bg-white/10"}`}
        >
          <span className="font-mono text-[10px] opacity-60 w-4">{String(idx + 1).padStart(2, "0")}</span>
          <i.icon className="h-4 w-4 shrink-0" />
          <span className="truncate">{i.label}</span>
          {i.countUrl && <NavCount url={i.countUrl} tid={`${i.tid}-count`} />}
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarBody({ mode, onNavigate }) {
  const { session } = useAuth();
  return (
    <div className="flex h-full flex-col sheet-bg text-white">
      <div className="px-5 py-5 flex items-center gap-2 border-b border-white/10">
        <div className="h-8 w-8 rounded-sm bg-emerald-400 text-emerald-950 flex items-center justify-center"><Wifi className="h-4 w-4" /></div>
        <div>
          <div className="font-display font-bold leading-none" data-testid="sidebar-app-name">{session?.app_name || "Bulk Nota"}</div>
          <div className="text-[10px] font-mono text-emerald-200 mt-1">{mode === "admin" ? "PANEL ADMIN UTAMA" : "MULTI-PERUSAHAAN ISP"}</div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-4">
        <NavList items={mode === "admin" ? ADMIN_NAV : TENANT_NAV} onNavigate={onNavigate} />
      </div>
    </div>
  );
}

function TenantStrip({ mode }) {
  const { tenant, user } = useAuth();
  if (mode === "admin") {
    return <div className="font-display font-semibold text-slate-900" data-testid="tenant-name">Panel {user?.role_label}</div>;
  }
  return (
    <div className="flex items-center gap-3 min-w-0" data-testid="tenant-header">
      {tenant?.logo ? (
        <img src={tenant.logo} alt="logo" className="h-9 w-9 object-contain border border-slate-200 rounded-sm bg-white" />
      ) : (
        <div className="h-9 w-9 rounded-sm bg-emerald-100 text-emerald-800 font-display font-bold flex items-center justify-center">{tenant?.nota_code}</div>
      )}
      <div className="min-w-0">
        <div className="font-display font-bold text-slate-900 truncate" data-testid="tenant-name">{tenant?.name}</div>
        <div className="text-[11px] text-slate-500 font-mono truncate hidden sm:block">KODE NOTA {tenant?.nota_code}{tenant?.extra_code} · {user?.platform_role ? <span className="text-emerald-700 font-semibold" data-testid="platform-access-badge">AKSES {user.role_label.toUpperCase()}</span> : user?.role_label}</div>
      </div>
    </div>
  );
}

export default function AppLayout({ mode, children }) {
  const { user, logout, session } = useAuth();
  useEffect(() => {
    if (session?.app_name) document.title = session.app_name;
  }, [session?.app_name]);
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  return (
    <div className="min-h-screen flex">
      <aside className="hidden lg:block w-64 shrink-0 fixed inset-y-0 left-0" data-testid="app-sidebar"><SidebarBody mode={mode} /></aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-64 border-0"><SidebarBody mode={mode} onNavigate={() => setOpen(false)} /></SheetContent>
      </Sheet>
      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-20 h-14 bg-white/90 backdrop-blur border-b border-slate-200 flex items-center gap-3 px-4 sm:px-6">
          <button className="lg:hidden h-10 w-10 flex items-center justify-center rounded hover:bg-slate-100" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
          <div className="flex-1 min-w-0"><TenantStrip mode={mode} /></div>
          {user?.platform_role && <TenantSwitcher />}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-100" data-testid="user-profile-menu">
                <div className="h-8 w-8 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center justify-center">{user?.name?.[0]?.toUpperCase()}</div>
                <div className="hidden sm:block text-left">
                  <div className="text-sm font-medium leading-tight">{user?.name}</div>
                  <div className="text-[11px] text-slate-500 leading-tight">@{user?.username}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel className="text-xs">{user?.role_label}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="menu-change-password"><KeyRound className="h-4 w-4 mr-2" />Ganti Password</DropdownMenuItem>
              <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }} data-testid="menu-logout"><LogOut className="h-4 w-4 mr-2" />Keluar</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1600px]">{children}</main>
      </div>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
